
import { defineComponent, ref, watch } from 'vue'
import { useBillingTemplateModule, BillingTemplatesListModel } from '@/modules/billing-templates'
import useQuasar from 'quasar/src/composables/use-quasar.js';
import { useRoute, useRouter } from 'vue-router'
import { OrganizationsRouteNames } from '@/router/routes-names'
import ConfirmDeletionDialog from '@/components/_shared/dialogs/confirm-deletion-dialog.vue'

export default defineComponent({
    name: 'billing-templates-list',
    props: {
        organizationId: {
            type: String,
            required: true
        }
    },
    async setup (props) {
        const router = useRouter()
        const route = useRoute()
        const $q = useQuasar()
        const {
            billingTemplatesList,
            getBillingTemplatesList,
            markBillingTemplateAsDefault,
            deleteBillingTemplate
        } = useBillingTemplateModule()

        const billingTemplatesColumns = ([
            { name: 'default', label: 'Default', field: 'isDefault', align: 'left' },
            { name: 'billingTemplateId', label: 'Id', field: 'billingTemplateId', align: 'left' },
            { name: 'templateName', label: 'Template name', field: 'templateName', align: 'left' },
            { name: 'organizationName', label: 'Organization name', field: 'organizationName', sortable: true, align: 'left' },
            { name: 'businessId', label: 'Business Id', field: 'businessId', align: 'left' },
            { name: 'actions', label: 'Actions', field: 'actions', align: 'left' }
        ])

        const visibleColumns = ['default', 'templateName', 'organizationName', 'businessId', 'actions']

        function onShowBillingTemplate (evt: PointerEvent | null, rowProps: BillingTemplatesListModel) {
            router.push({
                name: OrganizationsRouteNames.BILLING_TEMPLATE_SHOW,
                params: { organizationId: props.organizationId, billingTemplateId: rowProps.billingTemplateId as string },
                query: { ...route.query }
            })
        }

        async function onDeleteBillingTemplate (rowProps: BillingTemplatesListModel) {
            if (rowProps.isDefault) {
                $q.notify({
                    color: 'negative',
                    message: `Default billing template cannot be deleted. You need to assign another temple as default in order to delete ${rowProps.templateName}`
                })
                return
            }
            $q.dialog({
                component: ConfirmDeletionDialog,
                componentProps: {
                    entityType: 'billing template',
                    identifier: `"${rowProps.templateName}" for organization "${rowProps.organizationName}"`,
                    deleteCallback: async function () {
                        await deleteBillingTemplate(props.organizationId, rowProps.billingTemplateId)
                    }
                }
            })
        }

        const selectedBilling = ref<BillingTemplatesListModel[]>()

        const getDefaultBillingTemplate = async () => {
            await getBillingTemplatesList(props.organizationId)
            const defaultBilling = billingTemplatesList.value.find((template) => template.isDefault)
            return (defaultBilling ? [defaultBilling] : []) as BillingTemplatesListModel[]
        }

        const sendMarkAsDefaultRequest = async (billingTemplateId: string) => await markBillingTemplateAsDefault(props.organizationId, billingTemplateId, {
            billingTemplateId: billingTemplateId,
            organizationId: props.organizationId
        })

        watch(selectedBilling, () => {
            if (selectedBilling.value && selectedBilling.value.length > 0) {
                const [selected] = selectedBilling.value
                sendMarkAsDefaultRequest(selected.billingTemplateId)
            }
        })

        selectedBilling.value = await getDefaultBillingTemplate()

        const onCheckboxChange = (row: BillingTemplatesListModel, value: boolean) => {
            if (!value) {
                row.isDefault = true
                $q.notify({
                    color: 'negative',
                    message: 'At least one billing template must be set as default.'
                })
            } else {
                billingTemplatesList.value.forEach(template => {
                    if (template.billingTemplateId !== row.billingTemplateId) {
                        template.isDefault = false
                    }
                })
                row.isDefault = true
                sendMarkAsDefaultRequest(row.billingTemplateId)
            }
        }

        return {
            billingTemplatesList,
            billingTemplatesColumns,
            visibleColumns,
            onDeleteBillingTemplate,
            selectedBilling,
            onShowBillingTemplate,
            onCheckboxChange
        }
    }
})
